import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Building2, MapPin, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * About Us page — content is now driven by the About-Us CMS
 * (`GET /api/about`). Admins can edit copy from the admin dashboard.
 * Bilingual (AR/EN) with graceful fallback: if the active language field
 * is empty, we fall back to the Arabic version so the page never renders
 * blank.
 */
export const AboutPage = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/about`)
      .then((res) => {
        if (!cancelled) setContent(res.data);
      })
      .catch(() => {
        // API failure → keep content null; render skeleton (page still readable via defaults on retry)
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Pick localized value with AR fallback so we never render an empty string.
  const pick = (bilingual) => {
    if (!bilingual) return '';
    const primary = bilingual[language];
    if (primary && primary.trim()) return primary;
    return bilingual.ar || bilingual.en || '';
  };

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  if (!content) {
    return (
      <div className="min-h-screen bg-[#f4f7fb] flex items-center justify-center" data-testid="about-page-loading">
        <div className="text-slate-500 text-sm">...</div>
      </div>
    );
  }

  const header = content.header || {};
  const body = content.body || {};
  const vision = content.vision || {};
  const mission = content.mission || {};
  const closing = content.closing || {};
  const contact = content.contact || {};

  return (
    <div
      className={`min-h-screen bg-[#f4f7fb] ${dir === 'rtl' ? 'rtl' : 'ltr'}`}
      dir={dir}
      data-testid="about-page"
    >
      {/* Header band */}
      <div className="bg-gradient-to-b from-[#0d47a1] via-[#1565c0] to-[#1e88e5] text-white py-10 px-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Building2 size={28} strokeWidth={1.8} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-extrabold" data-testid="about-page-title">
              {pick(header.title)}
            </h1>
            <p className="text-blue-100 text-sm mt-1">{pick(header.subtitle)}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="p-6 md:p-10 leading-loose text-[#1f2d3d] text-[15px] md:text-base">
          <div className="text-center mb-6">
            <span className="inline-block text-[#1565c0] tracking-widest font-bold text-sm">
              {pick(body.kicker)}
            </span>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-[#0d47a1]">
              {pick(body.brand_name)}
            </h2>
            <div className="mt-3 h-1 w-16 bg-gradient-to-l from-[#1565c0] to-[#22c8b8] mx-auto rounded-full" />
          </div>

          {/* Intro paragraph */}
          <p className="mb-5 whitespace-pre-line" data-testid="about-intro">
            {pick(body.intro)}
          </p>

          {/* Paragraph 2 */}
          <p className="mb-5 whitespace-pre-line" data-testid="about-paragraph2">
            {pick(body.paragraph2)}
          </p>

          {/* Vision */}
          <div
            className={`bg-gradient-to-l from-blue-50 to-cyan-50 rounded-2xl p-5 my-6 ${
              dir === 'rtl' ? 'border-r-4' : 'border-l-4'
            } border-[#1565c0]`}
            data-testid="about-vision"
          >
            <h3 className="text-lg font-extrabold text-[#0d47a1] mb-2">{pick(vision.title)}</h3>
            <p className="whitespace-pre-line">{pick(vision.body)}</p>
          </div>

          {/* Mission */}
          <div
            className={`bg-gradient-to-l from-emerald-50 to-teal-50 rounded-2xl p-5 my-6 ${
              dir === 'rtl' ? 'border-r-4' : 'border-l-4'
            } border-[#22c8b8]`}
            data-testid="about-mission"
          >
            <h3 className="text-lg font-extrabold text-[#0d47a1] mb-2">{pick(mission.title)}</h3>
            <p className="whitespace-pre-line">{pick(mission.body)}</p>
          </div>

          {/* Closing */}
          <p
            className={`mt-6 italic text-[#0d47a1] font-medium ${
              dir === 'rtl' ? 'border-r-2 pr-4' : 'border-l-2 pl-4'
            } border-[#1565c0] whitespace-pre-line`}
            data-testid="about-closing"
          >
            {pick(closing.text)}
          </p>

          {/* Footer info chips */}
          <div className="mt-8 pt-6 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin size={16} className="text-[#1565c0] flex-shrink-0" />
              <span data-testid="about-address">{pick(contact.address)}</span>
            </div>
            {contact.website_url ? (
              <div className="flex items-center gap-2 text-gray-600">
                <Globe size={16} className="text-[#1565c0] flex-shrink-0" />
                <a
                  href={contact.website_url}
                  className="text-[#1565c0] font-bold hover:underline"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="about-website"
                >
                  {contact.website_display || contact.website_url}
                </a>
              </div>
            ) : null}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AboutPage;
